import { assertNever } from "@impero-com/common/utils/obj";

import { Codecs, PageFilterAndColumns } from "org/pageState/types";

import {
    ControlsAllPageStateWithType,
    decodeControlsAllPageState,
    encodeControlsAllPageState,
} from "./all";
import {
    ControlsHalfYearlyPageStateWithType,
    decodeControlsHalfYearlyPageState,
    encodeControlsHalfYearlyPageState,
} from "./halfYearly";
import {
    ControlsManualPageStateWithType,
    decodeControlsManualPageState,
    encodeControlsManualPageState,
} from "./manual";
import {
    ControlsMonthlyPageStateWithType,
    decodeControlsMonthlyPageState,
    encodeControlsMonthlyPageState,
} from "./monthly";
import {
    ControlsQuarterlyPageStateWithType,
    decodeControlsQuarterlyPageState,
    encodeControlsQuarterlyPageState,
} from "./quarterly";
import {
    ControlsTwoMonthsPageStateWithType,
    decodeControlsTwoMonthsPageState,
    encodeControlsTwoMonthsPageState,
} from "./twoMonths";
import {
    ControlsWeeklyPageStateWithType,
    decodeControlsWeeklyPageState,
    encodeControlsWeeklyPageState,
} from "./weekly";
import {
    ControlsYearlyPageStateWithType,
    decodeControlsYearlyPageState,
    encodeControlsYearlyPageState,
} from "./yearly";

type ControlsPageStateWithType =
    | ControlsAllPageStateWithType
    | ControlsHalfYearlyPageStateWithType
    | ControlsManualPageStateWithType
    | ControlsMonthlyPageStateWithType
    | ControlsQuarterlyPageStateWithType
    | ControlsTwoMonthsPageStateWithType
    | ControlsWeeklyPageStateWithType
    | ControlsYearlyPageStateWithType;

function encodeControlsPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsPageStateWithType {
    switch (filterAndColumns.pageType) {
        case "ControlsAll":
            return encodeControlsAllPageState(filterAndColumns);
        case "ControlsHalfYearly":
            return encodeControlsHalfYearlyPageState(filterAndColumns);
        case "ControlsManual":
            return encodeControlsManualPageState(filterAndColumns);
        case "ControlsMonthly":
            return encodeControlsMonthlyPageState(filterAndColumns);
        case "ControlsQuarterly":
            return encodeControlsQuarterlyPageState(filterAndColumns);
        case "ControlsTwoMonths":
            return encodeControlsTwoMonthsPageState(filterAndColumns);
        case "ControlsWeekly":
            return encodeControlsWeeklyPageState(filterAndColumns);
        case "ControlsYearly":
            return encodeControlsYearlyPageState(filterAndColumns);
        default:
            throw new Error(
                `encodeControlsPageState() cannot handle pageType=${filterAndColumns.pageType}`
            );
    }
}

function decodeControlsPageState(
    pageState: ControlsPageStateWithType
): PageFilterAndColumns {
    switch (pageState.type) {
        case "ControlsAll":
            return decodeControlsAllPageState(pageState);
        case "ControlsHalfYearly":
            return decodeControlsHalfYearlyPageState(pageState);
        case "ControlsManual":
            return decodeControlsManualPageState(pageState);
        case "ControlsMonthly":
            return decodeControlsMonthlyPageState(pageState);
        case "ControlsQuarterly":
            return decodeControlsQuarterlyPageState(pageState);
        case "ControlsTwoMonths":
            return decodeControlsTwoMonthsPageState(pageState);
        case "ControlsWeekly":
            return decodeControlsWeeklyPageState(pageState);
        case "ControlsYearly":
            return decodeControlsYearlyPageState(pageState);
        default:
            return assertNever(pageState);
    }
}

const pageStateControlsCodecs: Codecs<
    PageFilterAndColumns,
    ControlsPageStateWithType
> = {
    encode: encodeControlsPageState,
    decode: decodeControlsPageState,
};

export { pageStateControlsCodecs };
export type { ControlsPageStateWithType };
